import {
    DOUBLE_SPACING,
    MULTIPLE_CHOICE_QUESTIONS,
    PROBLEM_SOLVING,
    SINGLE_SPACING
} from '../../constants/PagesNumberConstant';
import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {Field, useForm, useFormState} from 'react-final-form';
import InputIntegerField from './InputIntegerField';
import {getSeconds} from '../../helpers/fieldRules';
import {useSelector} from "react-redux";
import {useIsShowMap} from "../../hooks/useIsShowMap";

export const getMaxLimit = (field, deadline, spacing, typeOfPaper, value, values = {}, limits = {}) => {
    const isDouble = String(spacing) === DOUBLE_SPACING;
    const isSingle = String(spacing) === SINGLE_SPACING;
    const paperTypeOk = typeOfPaper && typeOfPaper !== MULTIPLE_CHOICE_QUESTIONS;

    const seconds = Number(deadline);
    const is3h = seconds < getSeconds(8);
    const is8h = seconds >= getSeconds(8) && seconds < getSeconds(24);
    const is24h = seconds >= getSeconds(24);

    const page = values.page || 0;
    const problem = values.problem || 0;
    const visualPiece = values.visualPiece || 0;
    const source = values.source || 0;

    if (field === 'page' && paperTypeOk) {
        if (is3h && isDouble && value >= 3) return 3;
        if (is3h && isSingle && value >= 2) return 2;
        if (is8h && isSingle && value >= 5) return 5;
        if (is8h && isDouble && value >= 10) return 10;
    }

    if (field === 'slide') {
        if (is3h) return 0;
        if (is8h && value >= 10) return 10;
    }

    if (field === 'source' && is3h && source >= 2) {
        return 2;
    }

    if (is3h) {
        if (page === 0) {
            if (field === 'problem') {
                if (visualPiece > 3) return 0;
                if (visualPiece >= 1) return 1;
                return 2;
            }

            if (field === 'visualPiece') {
                if (problem > 1) return 0;
                if (problem === 1) return 2;
                return 4;
            }
        } else {
            if ((isDouble && page > 2) || (isSingle && page > 1)) {
                if (field === 'problem' || field === 'visualPiece') {
                    return 0;
                }
            }

            if ((isDouble && page < 3) || (isSingle && page < 2)) {
                if (field === 'problem') {
                    if (visualPiece > 0) return 0;
                    return 1;
                }

                if (field === 'visualPiece') {
                    if (problem > 0) return 0;
                    return 2;
                }
            }
        }
    }

    if (is8h) {
        if (page === 0) {
            if (field === 'problem') {
                if (visualPiece > 6) return 0;
                if (visualPiece > 4) return 1;
                if (visualPiece > 2) return 2;
                if (visualPiece > 0) return 3;
                return 4;
            }

            if (field === 'visualPiece') {
                if (problem === 4) return 0;
                if (problem > 2) return 2;
                if (problem === 2) return 4;
                if (problem === 1) return 6;
                return 8;
            }
        } else {
            // Заборона: >8 double або >4 single → problems + visuals = 0
            if ((isDouble && page > 8) || (isSingle && page > 4)) {
                if (field === 'problem' || field === 'visualPiece') {
                    return 0;
                }
            }

            // 7–8 double або 4 single
            else if ((isDouble && page > 6) || (isSingle && page === 4)) {
                if (field === 'problem') {
                    if (visualPiece > 0) return 0;
                    return 1;
                }
                if (field === 'visualPiece') {
                    if (problem > 0) return 0;
                    return 2;
                }
            }

            // 5–6 double або 3 single
            else if ((isDouble && page > 4) || (isSingle && page === 3)) {
                if (field === 'problem') {
                    if (visualPiece > 2) return 0;
                    if (visualPiece > 0) return 1;
                    return 2;
                }
                if (field === 'visualPiece') {
                    if (problem > 1) return 0;
                    if (problem === 1) return 2;
                    return 4;
                }
            }

            // <5 double або <3 single
            else {
                if (field === 'problem') {
                    if (visualPiece > 4) return 0;
                    if (visualPiece > 2) return 1;
                    if (visualPiece > 0) return 2;
                    return 3;
                }
                if (field === 'visualPiece') {
                    if (problem > 2) return 0;
                    if (problem === 2) return 2;
                    if (problem === 1) return 4;
                    return 6;
                }
            }
        }

        if (field === 'page') {
            if (isDouble && value > 10) return 10;
            if (isSingle && value > 5) return 5;
        }

        if (field === 'slide') {
            if (value > 10) return 10;
        }
    }

    if (is24h) {
        if (field === 'problem') {
            const maxByTotal = Math.floor((40 - visualPiece) / 2);
            return Math.max(0, Math.min(20, maxByTotal));
        }

        if (field === 'visualPiece') {
            const maxByTotal = 40 - problem * 2;
            return Math.max(0, Math.min(40, maxByTotal));
        }

        if (field === 'slide') {
            return 999;
        }
    }

    return (limits && limits[field] && limits[field].max) || 0;
};

function CountField({name, validate}) {
    const {limits} = useSelector(state => state.orderOptions.data);
    const form = useForm();
    const {values} = useFormState();
    const formValue = values[name];
    const {
        deadline,
        spacing,
        source,
        typeOfPaper,
        page,
        slide,
        problem,
        visualPiece
    } = values;

    const isVisible = useIsShowMap(values)[name] || false;
    if (!isVisible) return null;

    useEffect(() => {
        const timer = setTimeout(() => {
            const max = getMaxLimit(name, deadline, spacing, typeOfPaper, formValue, values, limits);
            const minVal = (limits && limits[name] && limits[name].min) || 0;

            if (!formValue || formValue < minVal) {
                form.change(name, minVal);
            }

            if (formValue > max) {
                form.change(name, max);
            }

            const seconds = Number(deadline);
            const is3h = seconds <= getSeconds(3);

            if (is3h || typeOfPaper === PROBLEM_SOLVING) {
                form.change('slide', 0);
            }

            if (name === 'source' && is3h && source > 2) {
                form.change(name, 2);
            }
        }, 0);

        return () => clearTimeout(timer);
    }, [formValue, deadline, spacing, typeOfPaper, page, slide, problem, visualPiece]);

    const change = value => {
        const newValue = parseInt(formValue || 0, 10) + value;
        form.change(name, newValue);
    };

    const plus = () => change(1);
    const minus = () => change(-1);

    const minValue = (limits && limits[name] && limits[name].min) || 0;
    const maxValue = getMaxLimit(name, deadline, spacing, typeOfPaper, formValue, values, limits);

    const canDecrement = Number(formValue) > minValue;
    const canIncrement = Number(formValue) < maxValue;

    return (
        <div>
            <div className="calculator-borders number-spinner">
                <button
                    type="button"
                    className="spinner-buttons minus"
                    onClick={minus}
                    disabled={!canDecrement}
                >
                    <svg width="16" height="2" viewBox="0 0 16 2" fill="none">
                        <path d="M15 1H8H1" stroke="#151515" strokeWidth="2" strokeLinecap="round"
                              strokeLinejoin="round"/>
                    </svg>
                </button>
                <div className="number-pages-container">
                    <Field
                        name={name}
                        component={InputIntegerField}
                        placeholder="Type here"
                        validate={validate}
                        className="number-pages control-input"
                        autoComplete="off"
                    />
                </div>
                <button
                    type="button"
                    className="spinner-buttons plus"
                    onClick={plus}
                    disabled={!canIncrement}
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 1V8M8 8V15M8 8H15M8 8H1" stroke="#151515" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                </button>
            </div>
        </div>
    );
}

CountField.propTypes = {
    name: PropTypes.string.isRequired,
    validate: PropTypes.func
};

export default CountField;
